import  {useCallback} from 'react';
import {TemplateEntityV1beta3} from '@backstage/plugin-scaffolder-common';
import {
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    Grid,
    makeStyles,
    Theme,
    Typography,
    useTheme
} from '@material-ui/core';
import {ItemCardHeader} from "@backstage/core-components";
import {FavoriteEntity, getEntityRelations} from '@backstage/plugin-catalog-react';
import {AppTemplateCardActions} from "./AppTemplateCardActions.tsx";
import {usePermission} from "@backstage/plugin-permission-react";
import {taskCreatePermission} from '@backstage/plugin-scaffolder-common/alpha';
import {configApiRef, IconComponent, useAnalytics, useApi} from "@backstage/core-plugin-api";
import AppTemplateCardLinks from "./AppTemplateCardLinks.tsx";
import {RELATION_OWNED_BY} from "@backstage/catalog-model";
import {AppTemplateDetailButton} from "./AppTemplateDetailButton.tsx";


const useStyles = makeStyles<Theme,
    {
        cardFontColor: string;
        cardBackgroundImage: string;
    }>((theme) => ({
    actionContainer: {
        padding: '16px',
        flex: 1,
        alignItems: 'flex-end'
    },
    header: {
        backgroundImage: ({cardBackgroundImage}: any) => cardBackgroundImage,
        color: ({cardFontColor}: any) => cardFontColor,
    },
    subtitleWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    detailIcon: {
        color: ({cardFontColor}) => cardFontColor,
    },
    imgContainer: {
        width: '50px',
        height: '50px',
        margin: '-24px 0 1px 13px',
        padding: '5px',
        borderRadius: '38px',
        borderStyle: 'solid',
        borderColor: theme.palette.background.default,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    imgWrapper: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    }
}));


export interface Props {
    template: TemplateEntityV1beta3;
    additionalLinks?: {
        icon: IconComponent;
        text: string;
        url: string;
    }[];
    onSelected?: (template: TemplateEntityV1beta3) => void;
}


export const AppTemplateCardComponent = (props: Props) => {
    const {additionalLinks, onSelected, template} = props;
    const type = template.spec.type;
    const ownedByRelations = getEntityRelations(template, RELATION_OWNED_BY);
    const baseUrl = useApi(configApiRef).getOptionalString('app.baseUrl');

    const tags = template.metadata.tags || [];
    const title = template.metadata.title || template.metadata.name;
    const description = template.metadata.description;
    const icon = template.metadata.annotations?.['backstage.io/icon'];

    const hasTags = !!tags.length;
    const displayDefaultDivider = !hasTags;
    const hasLinks =
        !!additionalLinks?.length || !!template.metadata.links?.length;

    const {getPageTheme} = useTheme();
    const themeForType = getPageTheme({themeId: type});
    const styles = useStyles({
        cardFontColor: themeForType.fontColor,
        cardBackgroundImage: themeForType.backgroundImage,
    });

    const analytics = useAnalytics();

    const SubtitleComponent = (
        <div className={styles.subtitleWrapper}>
            <div>{type}</div>
            <div>
                <AppTemplateDetailButton
                    className={styles.detailIcon}
                    template={props.template}
                />
                <FavoriteEntity entity={template} style={{padding: 0}}/>
            </div>
        </div>
    );

    const {allowed: canCreateTask} = usePermission({
        permission: taskCreatePermission,
    });

    const handleChoose = useCallback(() => {
        analytics.captureEvent('click', `Template has been opened`);
        onSelected?.(template);
    }, [analytics, onSelected, template]);

    return (
        <Card>
            <ItemCardHeader
                title={title}
                subtitle={SubtitleComponent}
                classes={{root: styles.header}}
            />
            <div className={styles.imgContainer}>
                <img
                    src={`${baseUrl}/assets/icon-${icon}.svg`}
                    alt={title}
                    className={styles.imgWrapper}
                />
            </div>
            <CardContent>
                <Grid container spacing={2} data-testid="template-card-content">
                    {description && (
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                {description}
                            </Typography>
                        </Grid>
                    )}

                    {displayDefaultDivider && (
                        <Grid item xs={12}>
                            <Divider data-testid="template-card-separator"/>
                        </Grid>
                    )}
                    {hasTags && (
                        <Grid item xs={12}>
                            <div>
                                {tags.map(tag => (
                                    <Chip key={tag} label={tag} size="small"/>
                                ))}
                            </div>
                        </Grid>
                    )}
                    {hasLinks && (
                        <AppTemplateCardLinks
                            template={template}
                            additionalLinks={additionalLinks}
                        />
                    )}
                </Grid>
            </CardContent>
            <CardActions
                className={styles.actionContainer}
                data-testid="template-card-actions"
            >
                <AppTemplateCardActions
                    canCreateTask={canCreateTask}
                    handleChoose={handleChoose}
                    ownedByRelations={ownedByRelations}
                />

            </CardActions>
        </Card>
    );
};
